import React, { useState, useEffect, useRef } from 'react';
import { DemoEmail } from '../../data/demoEmails';
import { useChats } from '../../contexts/ChatsContext';
import { useAuth } from '../../contexts/AuthContext';
import { chatWithAgent } from '../../services/agentService';

interface Props {
  email: DemoEmail;
  sessionId: string;
}

const STEPS = [
  { name: 'Intake',          dur: 14000 },
  { name: 'Positions',       dur: 15000 },
  { name: 'Doc reasoning',   dur: 14000 },
  { name: 'Enrichment',      dur: 13000 },
  { name: 'Draft & approve', dur: 16000 },
];

function toolBadge(text: string) {
  return `<div class="panel-bubble-tool"><span class="tool-tag">TOOL</span>${text}</div>`;
}
function doneBadge(text: string) {
  return `<span class="step-done" style="display:inline-block;background:#ECFDF3;color:#166534;font-size:10.5px;padding:1px 6px;border-radius:3px;margin-right:5px;font-weight:500">✓</span>${text}`;
}

const SYSTEM_COLORS: Record<string, string> = {
  'CA domain pack':     '#38BDF8',
  'Security Master':    '#A78BFA',
  'Tax Lot pack':       '#FB923C',
  'Money Movement pack':'#4ADE80',
};
function systemBadge(name: string) {
  const color = SYSTEM_COLORS[name] || '#94A3B8';
  const icon = `<svg width="11" height="11" viewBox="0 0 14 14" fill="none" style="flex-shrink:0;margin-top:1px"><ellipse cx="7" cy="4" rx="5" ry="2" stroke="${color}" stroke-width="1.4"/><path d="M2 4v6c0 1.1 2.24 2 5 2s5-.9 5-2V4" stroke="${color}" stroke-width="1.4"/><path d="M2 7c0 1.1 2.24 2 5 2s5-.9 5-2" stroke="${color}" stroke-width="1.4"/></svg>`;
  return `<span style="display:inline-flex;align-items:center;gap:5px;background:#0F172A;border:1px solid rgba(255,255,255,0.09);border-radius:20px;padding:3px 9px 3px 7px;font-size:11px;vertical-align:middle;margin:0 3px;line-height:1.2">${icon}<span style="color:${color};font-weight:700;font-family:monospace;letter-spacing:0.01em">${name}</span><span style="background:rgba(255,255,255,0.06);color:#64748B;font-size:9px;padding:1px 5px;border-radius:4px;font-family:monospace;font-weight:500;margin-left:2px">MCP</span></span>`;
}

function makeStepMsgs(email: DemoEmail) {
  return [
    // Step 0: Intake
    [
      { delay: 200,   content: `Parsing incoming email from <strong>${email.from}</strong>…` },
      { delay: 2200,  content: toolBadge('Intent: <strong>tender offer eligibility</strong> for advisor book') },
      { delay: 4400,  content: `Disambiguating: 2 active ${email.companyName} offers → matched to <strong>${email.ticker} 14.00 cash tender, exp. 06/12/2026</strong>` },
      { delay: 7000,  content: `Calling ${systemBadge('CA domain pack')} via MCP gateway` },
      { delay: 8200,  content: `Calling ${systemBadge('Security Master')} via MCP gateway` },
      { delay: 10500, content: doneBadge('Intent resolved. Eligibility evaluation starting.') },
    ],
    // Step 1: Position retrieval
    [
      { delay: 200,   content: `${systemBadge('CA domain pack')} joining 18 accounts against DataXChange positions…` },
      { delay: 2400,  content: toolBadge('Applying eligibility rules: holder of record, min quantity, jurisdiction filters') },
      { delay: 4800,  content: '<strong>12</strong> accounts eligible · <strong>3</strong> ineligible (min qty / jurisdiction) · <strong>15</strong> total positions evaluated' },
      { delay: 7200,  content: '<strong>2</strong> accounts qualify for early-tender premium (deadline T-3 days)' },
      { delay: 10200, content: doneBadge('Position retrieval complete. Reading offer document.') },
    ],
    // Step 2: Document reasoning
    [
      { delay: 200,   content: `Loading ${email.companyName} Offer to Purchase (47 pp)…` },
      { delay: 2200,  content: toolBadge('Extracting: offer price, expiration, premium terms, conditions') },
      { delay: 5000,  content: 'Cross-checking eligibility against section 4.2 (min qty) and section 7.1 (jurisdiction). <strong>Matches CA domain pack output</strong>' },
      { delay: 7800,  content: toolBadge('<strong>Section 9.4</strong>: non-standard proration clause detected. Flagging for human review.') },
      { delay: 9800,  content: '2 accounts within early-tender window. Computing premium economics.' },
      { delay: 11600, content: doneBadge('Document reasoning complete. Enriching with tax & basis.') },
    ],
    // Step 3: Cross-domain enrichment
    [
      { delay: 200,   content: `Calling ${systemBadge('Tax Lot pack')} for cost basis on 12 eligible accounts` },
      { delay: 2400,  content: '10 of 12 positions held &gt;1 yr → <strong>long-term capital gains treatment</strong>' },
      { delay: 4400,  content: `${systemBadge('Money Movement pack')} settlement instructions verified · no operational holds` },
      { delay: 6200,  content: `${systemBadge('Security Master')} CUSIP active · DTCC-eligible · no transfer restrictions` },
      { delay: 8200,  content: `Historical: ${email.from.split(' ')[0]}'s book participated in <strong>2 prior ${email.companyName} actions</strong>, both fully tendered` },
      { delay: 10000, content: doneBadge('Enrichment complete. Drafting advisor response.') },
    ],
    // Step 4: Synthesis
    [
      { delay: 200,  content: 'Synthesizing advisor-ready response…' },
      { delay: 2400, content: 'Drafting summary: eligibility, tax treatment, early-tender economics' },
    ],
  ];
}

const ACCOUNTS = [
  { id: 'NP-44021', shares: '4,200 sh',  status: 'Eligible',                  type: 'ok'   },
  { id: 'NP-44087', shares: '8,000 sh',  status: 'Eligible · Early-tender',   type: 'flag' },
  { id: 'NP-44103', shares: '1,500 sh',  status: 'Eligible',                  type: 'ok'   },
  { id: 'NP-44156', shares: '12,400 sh', status: 'Eligible · Early-tender',   type: 'flag' },
  { id: 'NP-44209', shares: '600 sh',    status: 'Ineligible · Min qty',      type: 'no'   },
  { id: 'NP-44231', shares: '3,100 sh',  status: 'Eligible',                  type: 'ok'   },
  { id: 'NP-44288', shares: '900 sh',    status: 'Ineligible · Jurisdiction', type: 'no'   },
];

const STATUS_CLS: Record<string, string> = {
  ok:   'bg-green-50 text-green-700',
  flag: 'bg-orange-50 text-orange-700',
  no:   'bg-red-50 text-red-700',
};

function StepProgress({ current, viewing, onStepClick }: {
  current: number;
  viewing: number;
  onStepClick: (i: number) => void;
}) {
  return (
    <div className="px-5 pt-4 pb-4 border-b border-trim">
      <div className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-4">
        Processing Pipeline
      </div>
      <div className="flex items-start">
        {STEPS.map((s, i) => {
          const isClickable = i <= current;
          const isViewing = i === viewing && i !== current;
          return (
            <React.Fragment key={s.name}>
              <div
                className={`flex flex-col items-center ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                style={{ width: 80, flexShrink: 0 }}
                onClick={() => isClickable && onStepClick(i)}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                  isViewing ? 'ring-2 ring-offset-1 ring-teal-400' : ''
                } ${
                  i < current
                    ? 'bg-green-500 text-white hover:opacity-80'
                    : i === current
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'bg-wash text-muted border border-trim'
                }`}>
                  {i < current ? (
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    String(i + 1)
                  )}
                </div>
                <div className={`text-[11px] mt-1.5 text-center leading-tight px-0.5 ${
                  isViewing ? 'text-teal-700 font-semibold' :
                  i < current ? 'text-green-700 font-medium' :
                  i === current ? 'text-ink font-semibold' : 'text-muted'
                }`}>
                  {s.name}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-px transition-colors ${i < current ? 'bg-green-400' : 'bg-trim'}`}
                  style={{ marginTop: 14 }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function GovernanceStrip({ text }: { text: string }) {
  return (
    <div className="mx-4 mt-3 mb-1 flex items-start gap-2 bg-wash border border-trim rounded-lg px-3 py-2">
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className="flex-shrink-0 mt-0.5 text-muted">
        <path d="M7 1L2 4v3c0 3 2.5 5.5 5 6 2.5-.5 5-3 5-6V4L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
      <span className="text-xs text-muted leading-snug">{text}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold text-orange-700 uppercase tracking-widest bg-orange-50 px-4 py-2">
      {children}
    </div>
  );
}

// ── Step artifact components ──────────────────────────────────────────────────

function Step0Artifact({ email }: { email: DemoEmail }) {
  return (
    <div>
      <SectionLabel>Incoming email</SectionLabel>
      <div className="mx-4 my-3 border border-trim rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-wash border-b border-trim">
          <div className="text-xs font-semibold text-ink">{email.from} &lt;{email.fromEmail}&gt;</div>
          <div className="text-xs text-muted mt-0.5">To: {email.to} · {email.date}</div>
        </div>
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-ink mb-2">{email.subject}</div>
          <div className="text-sm text-copy leading-relaxed whitespace-pre-line">{email.body}</div>
        </div>
      </div>
      <GovernanceStrip text="ace.governance · session opened · advisor identity verified · entitlements checked" />
    </div>
  );
}

function Step1Artifact() {
  return (
    <div>
      <div className="mx-4 mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-1">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-green-600 flex-shrink-0">
          <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-xs font-semibold text-green-700">18 positions retrieved · 12 eligible · 3 ineligible · 2 early-tender</span>
      </div>
      <SectionLabel>Position eligibility</SectionLabel>
      <div className="mx-4 my-3 border border-trim rounded-xl overflow-hidden">
        <div className="grid px-3 py-2 bg-gray-50 border-b border-trim text-xs font-semibold text-muted uppercase tracking-wider" style={{ gridTemplateColumns: '1fr 90px 180px' }}>
          <div>Account</div>
          <div>Position</div>
          <div>Status</div>
        </div>
        {ACCOUNTS.map(a => (
          <div key={a.id} className="grid items-center px-3 py-2.5 border-b border-trim last:border-b-0" style={{ gridTemplateColumns: '1fr 90px 180px' }}>
            <div className="font-medium text-ink text-xs">{a.id}</div>
            <div className="text-xs text-muted">{a.shares}</div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded w-fit ${STATUS_CLS[a.type]}`}>{a.status}</span>
          </div>
        ))}
        <div className="px-3 py-2 text-xs text-muted italic">+ 11 more accounts evaluated</div>
      </div>
      <GovernanceStrip text="Snowflake CA domain pack · DataXChange position data · semantic rules v4.2 · query logged" />
    </div>
  );
}

function Step2Artifact() {
  return (
    <div>
      <SectionLabel>Offer document analysis</SectionLabel>
      <div className="mx-4 my-3 border border-trim rounded-xl overflow-hidden">
        <div className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-3 border-b border-trim">
          {[
            { label: 'Document',             val: 'Acme_Tender_OTP.pdf · 47 pages' },
            { label: 'Offer price',           val: '$14.00 / sh cash' },
            { label: 'Expiration',            val: 'June 12, 2026 · 5:00 PM ET' },
            { label: 'Early-tender premium',  val: '+$0.50 / sh · by May 22' },
          ].map(({ label, val }) => (
            <div key={label}>
              <div className="text-xs text-muted mb-0.5">{label}</div>
              <div className="text-xs font-semibold text-ink">{val}</div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3">
          <div className="text-xs text-muted mb-2 font-medium uppercase tracking-wider">Edge cases identified</div>
          <div className="flex flex-col gap-2">
            {[
              { tag: 'FLAG',   text: 'Section 4.2 — minimum 100 sh per holder (NP-44209 ineligible)' },
              { tag: 'FLAG',   text: 'Section 7.1 — excludes holders in OH, MI, IN (NP-44288 ineligible)' },
              { tag: 'REVIEW', text: 'Section 9.4 — non-standard proration clause if >65% tendered' },
            ].map(({ tag, text }) => (
              <div key={text} className="flex gap-2 items-start">
                <span className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded bg-orange-50 text-orange-700">{tag}</span>
                <span className="text-xs text-copy leading-snug">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <GovernanceStrip text="ace.ai reasoning agent · 47-page prospectus parsed · 3 provisions flagged for review" />
    </div>
  );
}

function Step3Artifact() {
  const checks = [
    { agent: 'Tax Lot agent',          detail: 'Cost basis lookup · 12 accounts · avg basis $9.40' },
    { agent: 'Money Movement agent',   detail: 'Settlement instructions verified · no holds' },
    { agent: 'Security Master agent',  detail: 'CUSIP confirmed · DTCC-eligible · no restrictions' },
    { agent: 'CA history',             detail: '2 prior Acme actions, both fully tendered' },
  ];
  const stats = [
    { label: 'Avg gain at $14.00', val: '+$4.60/sh' },
    { label: 'LT capital gains',   val: '10 of 12' },
    { label: 'Early-tender uplift', val: '+$10,200' },
  ];
  return (
    <div>
      <SectionLabel>Cross-domain enrichment</SectionLabel>
      <div className="mx-4 mt-3 border border-trim rounded-xl overflow-hidden">
        {checks.map((c, i) => (
          <div key={c.agent} className={`flex items-start gap-3 px-4 py-3 ${i < checks.length - 1 ? 'border-b border-trim' : ''}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted">{c.agent}</div>
              <div className="text-xs font-medium text-ink mt-0.5 leading-snug">{c.detail}</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-green-500 flex-shrink-0 mt-0.5">
              <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        ))}
      </div>
      <div className="mx-4 mt-2 grid grid-cols-3 gap-2">
        {stats.map(s => (
          <div key={s.label} className="bg-wash border border-trim rounded-xl px-3 py-2.5">
            <div className="text-xs text-muted leading-tight">{s.label}</div>
            <div className="text-base font-semibold text-ink mt-1">{s.val}</div>
          </div>
        ))}
      </div>
      <GovernanceStrip text="Master MCP Gateway · 3 domain pack calls · auth tokens passed · all entitlements validated" />
    </div>
  );
}

function Step4Artifact({ email, draftText, approved, canApprove, onApprove, onReject, onDraftChange }: {
  email: DemoEmail;
  draftText: string;
  approved: boolean;
  canApprove: boolean;
  onApprove: () => void;
  onReject: () => void;
  onDraftChange: (text: string) => void;
}) {
  if (approved) {
    return (
      <div className="mx-4 my-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-4">
        <svg width="18" height="18" viewBox="0 0 14 14" fill="none" className="text-green-600 flex-shrink-0">
          <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <div>
          <div className="text-sm font-semibold text-green-800">Response sent to {email.from}</div>
          <div className="text-xs text-green-700 mt-0.5">Routed to legal for section 9.4 review · Session paused · Listening for follow up emails</div>
        </div>
      </div>
    );
  }

  if (!draftText) {
    return (
      <div className="mx-4 my-4">
        <SectionLabel>Synthesis & approval</SectionLabel>
        <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted">
          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
          Generating advisor response…
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 pb-4">
      <SectionLabel>Draft response — awaiting approval</SectionLabel>
      <div className="mx-4 my-3 flex-1 flex min-h-0">
        <textarea
          className="w-full h-full bg-white border border-trim rounded-xl px-4 py-3 text-sm text-ink leading-relaxed resize-none outline-none focus:border-teal-400 transition-colors font-[inherit]"
          style={{ minHeight: 220 }}
          value={draftText}
          onChange={e => onDraftChange(e.target.value)}
        />
      </div>
      {canApprove ? (
        <div className="mx-4 flex gap-2 mb-3">
          <button
            onClick={onApprove}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ background: 'linear-gradient(135deg,#14b8a6,#0891b2)' }}
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Approve & send
          </button>
          <button
            onClick={onReject}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Reject
          </button>
        </div>
      ) : (
        <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          Sending this response requires a Team Manager — your role can prepare but not approve.
        </div>
      )}
      <GovernanceStrip text="Reasoning trace · 47 steps · view in ace.governance" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function CAWorkflowPanel({ email, sessionId }: Props) {
  const { sessions, appendMsg, updateSession } = useChats();
  const { can } = useAuth();
  const session = sessions.find(s => s.id === sessionId);
  const alreadyComplete = session?.caWorkflowComplete === true;
  const savedDraft = session?.caDraftText || '';
  const savedApproved = session?.caApproved === true;

  const [step, setStep] = useState<number>(alreadyComplete ? 4 : -1);
  const [viewStep, setViewStep] = useState<number | null>(null);
  const [draftText, setDraftText] = useState(savedDraft);
  const [approved, setApproved] = useState(savedApproved);
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);

  const startedRef = useRef(false);
  const appendMsgRef = useRef(appendMsg);
  const sessionIdRef = useRef(sessionId);
  const updateSessionRef = useRef(updateSession);
  appendMsgRef.current = appendMsg;
  sessionIdRef.current = sessionId;
  updateSessionRef.current = updateSession;

  const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const stepMsgs = makeStepMsgs(email);

  // Kick off workflow once on mount
  useEffect(() => {
    if (startedRef.current || alreadyComplete) return;
    startedRef.current = true;
    setStep(0);
  }, [alreadyComplete]);

  // Handle each step: post messages, advance, and trigger Claude at step 4
  useEffect(() => {
    if (step < 0) return;
    const timers: ReturnType<typeof setTimeout>[] = [];

    stepMsgs[step]?.forEach(({ delay, content }) => {
      timers.push(setTimeout(() => {
        appendMsgRef.current(sessionIdRef.current, {
          id: String(Date.now() + Math.random()),
          role: 'agent',
          content,
          time: now(),
          trace: !content.includes('step-done'),
        });
      }, delay));
    });

    if (step < STEPS.length - 1) {
      timers.push(setTimeout(() => setStep(s => s + 1), STEPS[step].dur));
    }

    if (step === 4) {
      timers.push(setTimeout(async () => {
        try {
          const resp = await chatWithAgent(
            `Draft a concise advisor-facing response to this email:\n\nFrom: ${email.from} <${email.fromEmail}>\nSubject: ${email.subject}\n\n${email.body}\n\nUse these confirmed facts:\n- 12 accounts eligible to tender at $14.00/sh\n- 3 accounts ineligible: NP-44209 (below 100 sh minimum per section 4.2), NP-44288 and one other (OH/MI/IN jurisdiction exclusion per section 7.1)\n- 2 accounts qualify for early-tender premium (+$0.50/sh, deadline May 22): NP-44087 and NP-44156. Combined uplift: +$10,200\n- 10 of 12 eligible positions held >1 year → long-term capital gains. Average gain at $14.00: +$4.60/sh\n- Section 9.4 non-standard proration clause flagged for legal review (activates if >65% tendered)\n\nWrite a clear, professional response addressed to the advisor.`,
            'corporate-actions',
            []
          );
          const draft = resp.message;
          setDraftText(draft);
          updateSessionRef.current(sessionIdRef.current, { caDraftText: draft, caWorkflowComplete: true });
          appendMsgRef.current(sessionIdRef.current, {
            id: String(Date.now() + Math.random()),
            role: 'agent',
            content: doneBadge('Draft ready. Routing to human reviewer for approval.'),
            time: now(),
          });
        } catch {
          const fallback = `Hi ${email.from.split(' ')[0]} —\n\nI've reviewed all 18 of your client positions in ${email.companyName} against the tender offer. Here's the summary:\n\n• 12 accounts eligible to tender at $14.00/sh cash\n• 3 accounts ineligible: NP-44209 (below 100 sh minimum per section 4.2), NP-44288 and one other (OH/MI/IN jurisdiction exclusion per section 7.1)\n• 2 accounts qualify for the early-tender premium (+$0.50/sh, deadline May 22) — NP-44087 and NP-44156. Combined uplift if tendered early: +$10,200.\n• 10 of 12 eligible positions held >1 year → long-term capital gains treatment. Average gain at offer price: +$4.60/sh.\n\nOne item flagged for review: Section 9.4 contains a non-standard proration clause that activates if >65% of shares are tendered. I've routed this to legal for sign-off before submitting elections.`;
          setDraftText(fallback);
          updateSessionRef.current(sessionIdRef.current, { caDraftText: fallback, caWorkflowComplete: true });
          appendMsgRef.current(sessionIdRef.current, {
            id: String(Date.now() + Math.random()),
            role: 'agent',
            content: doneBadge('Draft ready. Routing to human reviewer for approval.'),
            time: now(),
          });
        }
      }, 3500));
    }

    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function handleApprove() {
    setApproved(true);
    setShowEmailSentModal(true);
    updateSession(sessionId, { caApproved: true });
    const ts = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    appendMsg(sessionId, {
      id: String(Date.now() + Math.random()),
      role: 'agent',
      content:
        doneBadge(`Response <strong>approved & sent</strong> to ${email.from} &lt;${email.fromEmail}&gt;`) +
        `<br><div style="margin-top:8px;background:#ECFDF3;border:1px solid #BBF7D0;border-radius:8px;padding:10px 12px;font-size:12px">` +
        `<div style="font-weight:600;color:#166534;margin-bottom:3px">Decision recorded · ${ts}</div>` +
        `<div style="color:#15803D;line-height:1.5">Advisor notified · section 9.4 proration clause routed to legal · Session archived</div>` +
        `</div>`,
      time: now(),
    });
  }

  function handleReject() {
    appendMsg(sessionId, {
      id: String(Date.now() + Math.random()),
      role: 'agent',
      content: 'Draft rejected. Please revise the response or route to a supervisor.',
      time: now(),
    });
  }

  const actualStep = step < 0 ? 0 : step;
  const displayStep = viewStep !== null ? viewStep : actualStep;

  function handleStepClick(i: number) {
    setViewStep(i === actualStep ? null : i);
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Panel title */}
      <div className="px-5 pt-4 pb-3 border-b border-trim">
        <div className="text-sm font-semibold text-ink">{email.companyName} — {email.ticker}</div>
        <div className="text-xs text-muted mt-0.5">From: {email.from} · {email.date}</div>
      </div>

      <StepProgress current={actualStep} viewing={displayStep} onStepClick={handleStepClick} />

      <div className="flex-1 flex flex-col min-h-0">
        {displayStep === 0 && <Step0Artifact email={email} />}
        {displayStep === 1 && <Step1Artifact />}
        {displayStep === 2 && <Step2Artifact />}
        {displayStep === 3 && <Step3Artifact />}
        {displayStep === 4 && (
          <Step4Artifact
            email={email}
            draftText={draftText}
            approved={approved}
            canApprove={can('draft.approve')}
            onApprove={handleApprove}
            onReject={handleReject}
            onDraftChange={text => {
              setDraftText(text);
              updateSessionRef.current(sessionIdRef.current, { caDraftText: text });
            }}
          />
        )}
      </div>

      {/* Email sent confirmation modal */}
      {showEmailSentModal && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowEmailSentModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl px-8 py-7 flex flex-col items-center gap-4 max-w-xs w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M6 16l7 7L26 9" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-ink">Email sent</div>
              <div className="text-sm text-muted mt-1.5">
                Response delivered to <strong className="text-ink">{email.from}</strong>
              </div>
              <div className="text-xs text-muted mt-1">Routed to legal for section 9.4 review · session archived</div>
            </div>
            <button
              onClick={() => setShowEmailSentModal(false)}
              className="w-full py-2.5 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
